import * as React from 'react';

import {Field} from 'redux-form';

import {ReplyBanner} from 'shells/email-editor/reply-banner';

import {AiFeatureType} from 'nutshell-graphql-types';
import type {
    GetSuggestedReplies_suggestedReplies as SuggestedReplies,
    GetSuggestedReplies_suggestedReplies_replies as SuggestedReply,
} from 'nutshell-graphql-types';

import {useIsAiFeatureEnabled} from '../../../setup/ai-features/graphql/hooks/use-is-ai-feature-enabled';

import {BodyField} from './body-field';
import {MissingFieldsAlert} from './missing-fields-alert';
import {SubjectField} from './subject-field';
import {SuggestedRepliesSection} from './suggested-replies-section';

import styles from './email-form-section.module.css';

const EMPTY_BODY = '<div><br></div>';

type OnBodyChange = (input: {
    onChange: (...args: any) => any;
}) => (newHtml: string, newText: string) => void;

type Props = {
    isActive: boolean;
    isMultiSequence: boolean;
    onBodyChange: OnBodyChange;
    onSubjectChange?: (arg1: string) => void;
    sendAsReply: boolean;
    isLoading?: boolean;
    isBodyFocused?: boolean;
    autoFocusSubject?: boolean;
    fileAttachmentFieldName?: string | null | undefined;
    suggestedReplies?: SuggestedReplies;
    isLoadingSuggestedReplies?: boolean;
    emailReplyId?: string;
    selectedSuggestedReplyId?: string;
};

export const PLACEHOLDER_DISABLE_TEXT =
    'This template has custom placeholders that must be filled in before sending your email.';

// @ts-expect-error
const requiredSubject = (value) =>
    !value || value.trim().length === 0 ? 'What is this email about?' : undefined;
// @ts-expect-error
const requiredBody = (value) =>
    !value || value === EMPTY_BODY ? 'What should this email say?' : undefined;
// @ts-expect-error
const noPlaceholders = (value) => valueContainsNonEmptyPlaceholders(value);

export function EmailFormSection(props: Props) {
    const [isBodyFocused, setIsBodyFocused] = React.useState<boolean>(false);
    const [streamContent, setLatestStreamContent] = React.useState<string>('');
    const [suggestedReplyToInsert, setSuggestedReplyToInsert] = React.useState<
        string | null | undefined
    >(null);

    const {fileAttachmentFieldName = 'fileAttachments', autoFocusSubject = true} = props;

    const {isEnabled: isEmailReplyStarterAssistEnabled} = useIsAiFeatureEnabled(
        AiFeatureType.EMAIL_REPLY_STARTER
    );
    const isEmailReplyStarterEnabled = NutClientConfig.hasAiOutcomesFF
        ? isEmailReplyStarterAssistEnabled
        : NutClientConfig.enableAiEmailReplies;

    React.useEffect(() => {
        if (streamContent.length) {
            // TODO: Update this component externally
            setLatestStreamContent('');
        }
    }, [streamContent]);

    function handleSuggestedReplyInserted(reply: SuggestedReply) {
        setSuggestedReplyToInsert(reply.body);
    }

    return (
        <div
            // This div is targeted for recipient menu portal
            id='email-subject-and-body-fields'
            style={
                props.isActive
                    ? undefined
                    : // When not active, hide and remove from flex-flow
                      {visibility: 'hidden', position: 'absolute'}
            }
            className={styles['email-form-section']}
        >
            <Field
                name='subject'
                component={SubjectField}
                onSubjectChange={props.onSubjectChange}
                validate={[requiredSubject]}
                isDisabled={props.sendAsReply}
                isMultiSequence={props.isMultiSequence}
                autoFocus={props.isBodyFocused ? false : autoFocusSubject}
            />
            {isEmailReplyStarterEnabled &&
                NutClientConfig.hasAiEmailReplies &&
                props.emailReplyId && (
                    <SuggestedRepliesSection
                        suggestedReplies={props.suggestedReplies}
                        selectedSuggestedReplyId={props.selectedSuggestedReplyId}
                        onSuggestedReplyInserted={handleSuggestedReplyInserted}
                        isLoading={props.isLoadingSuggestedReplies}
                    />
                )}
            <div className={isBodyFocused ? styles['body--focused'] : styles.body}>
                <Field
                    name='body'
                    isActive={props.isActive}
                    isFocused={isBodyFocused}
                    isBodyFocused={props.isBodyFocused}
                    onFocus={() => setIsBodyFocused(true)}
                    onBlur={() => setIsBodyFocused(false)}
                    onBodyChange={props.onBodyChange}
                    fileAttachmentFieldName={fileAttachmentFieldName}
                    notificationBanner={props.sendAsReply ? ReplyBanner : undefined}
                    component={StableBodyField}
                    validate={[requiredBody, noPlaceholders]}
                    isLoading={props.isLoading}
                    suggestedReplyToInsert={suggestedReplyToInsert}
                />
            </div>
            <Field name='missingFieldsMessage' component={MissingFieldsAlert} />
        </div>
    );
}

/*
 * Function that's responsible for taking the email input, turning it into
 * an HTML document, and checking for non-empty placeholder spans.
 *
 * Previously, we'd just look at the email input string and any opening <span>
 * tags with the correct placeholder classname, assuming that if they existed
 * they needed to be replaced, but we've found that sometimes they can get deleted
 * visually, but still linger in the document. In this case, they're "empty"
 * so we can technically send this email still.
 *
 * See: https://nutshell.atlassian.net/browse/NUT-13963
 */
function valueContainsNonEmptyPlaceholders(value: string): string | null | undefined {
    const parser = new DOMParser();

    // Use native DOMParser API to turn our email into HTML
    const emailHtmlDocument = parser.parseFromString(value, 'text/html');

    // Grab all placeholders blots from the document
    const placeholderBlotElements = emailHtmlDocument.querySelectorAll(
        '.ql-placeholder-blot-custom'
    );

    // Gotta convert NodeList into an array
    const placeholderBlotElementArray = Array.from(placeholderBlotElements);

    // Filter out all "empty" placeholder blots that might've stuck around
    // due to Quill's delta code being jank AF
    //
    // We'll sometimes see something like
    // <span class="ql-placeholder-blot-custom">#65279</span> (empty space unicode)
    // linger around, so it's not quite good enough to just look for the opening <span>
    const nonEmptyPlaceholderBlots = placeholderBlotElementArray.filter(
        (placeholderBlotElement) => {
            // @ts-expect-error
            if (placeholderBlotElement.innerText) {
                // @ts-expect-error
                return placeholderBlotElement.innerText.trim().length > 0;
            }

            return false;
        }
    );

    // If any non empty placeholders, return validation error, otherwise we're good!
    return nonEmptyPlaceholderBlots.length ? PLACEHOLDER_DISABLE_TEXT : undefined;
}

// Memoize the BodyField and prevent re-render when suggestedReplies prop
// changes, which was previously causing an issue where any typing while the
// component re-rendered was not captured.
const StableBodyField = React.memo(BodyField, (prevProps, nextProps) => {
    // Clone the props so we can modify them locally here
    const prevClean = {...prevProps} as const;
    const nextClean = {...nextProps} as const;

    // Remove the props we don't want causing re-renders
    prevClean.suggestedReplies = null;
    prevClean.isLoadingSuggestedReplies = null;
    prevClean.selectedSuggestedReplyId = null;

    nextClean.suggestedReplies = null;
    nextClean.isLoadingSuggestedReplies = null;
    nextClean.selectedSuggestedReplyId = null;

    return JSON.stringify(prevClean) === JSON.stringify(nextClean);
});
